import PropTypes from "prop-types";
import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import ReactModal from "react-modal";
import VM from "scratch-vm";
import { injectIntl, intlShape } from "react-intl";

import ErrorBoundaryHOC from "../lib/error-boundary-hoc.jsx";
import { getIsError, getIsShowingProject } from "../reducers/project-state";
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX,
} from "../reducers/editor-tab";

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeTelemetryModal,
    openExtensionLibrary,
    closeDebugModal,
} from "../reducers/modals";

import FontLoaderHOC from "../lib/font-loader-hoc.jsx";
import LocalizationHOC from "../lib/localization-hoc.jsx";
import SBFileUploaderHOC from "../lib/sb-file-uploader-hoc.jsx";
import ProjectFetcherHOC from "../lib/project-fetcher-hoc.jsx";
import TitledHOC from "../lib/titled-hoc.jsx";
import ProjectSaverHOC from "../lib/project-saver-hoc.jsx";
import QueryParserHOC from "../lib/query-parser-hoc.jsx";
import storage from "../lib/storage";
import vmListenerHOC from "../lib/vm-listener-hoc.jsx";
import vmManagerHOC from "../lib/vm-manager-hoc.jsx";
import cloudManagerHOC from "../lib/cloud-manager-hoc.jsx";
import systemPreferencesHOC from "../lib/system-preferences-hoc.jsx";

import GUIComponent from "../components/gui/gui.jsx";
import { setIsScratchDesktop } from "../lib/isScratchDesktop.js";

const { RequestMetadata, setMetadata, unsetMetadata } = storage.scratchFetch;

const setProjectIdMetadata = (projectId) => {
    // If project ID is '0' or zero, it's not a real project ID. In that case, remove the project ID metadata.
    // Same if it's null undefined.
    if (projectId && projectId !== "0") {
        setMetadata(RequestMetadata.ProjectId, projectId);
    } else {
        unsetMetadata(RequestMetadata.ProjectId);
    }
};

class GUI extends React.Component {
    componentDidMount() {
        setIsScratchDesktop(this.props.isScratchDesktop);
        this.props.onStorageInit(storage);
        this.props.onVmInit(this.props.vm);
        setProjectIdMetadata(this.props.projectId);
        
        // 补丁：覆盖 installTargets 方法，使扩展加载失败不阻塞项目加载
        this.patchVMInstallTargets(this.props.vm);
    }
    
    // 补丁方法：让扩展加载失败不阻塞项目加载
    patchVMInstallTargets(vm) {
        const originalInstallTargets = vm.installTargets.bind(vm);
        
        vm.installTargets = function(targets, extensions, wholeProject) {
            const extensionPromises = [];
            
            extensions.extensionIDs.forEach(extensionID => {
                if (!vm.extensionManager.isExtensionLoaded(extensionID)) {
                    const extensionURL = extensions.extensionURLs.get(extensionID) || extensionID;
                    // 捕获扩展加载错误，不阻塞项目加载
                    const extensionPromise = vm.extensionManager.loadExtensionURL(extensionURL)
                        .catch(error => {
                            console.warn(`[DEBUG] 扩展 ${extensionID} 加载失败（不影响项目加载）:`, error);
                            // 返回一个已解决的 Promise，这样不会阻塞 Promise.all
                            return Promise.resolve();
                        });
                    extensionPromises.push(extensionPromise);
                }
            });
            
            targets = targets.filter(target => !!target);
            
            return Promise.all(extensionPromises).then(() => {
                targets.forEach(target => {
                    vm.runtime.addTarget(target);
                    target.updateAllDrawableProperties();
                    // Ensure unique sprite name
                    if (target.isSprite()) vm.renameSprite(target.id, target.getName());
                });
                // Sort the executable targets by layerOrder.
                // Remove layerOrder property after use.
                vm.runtime.executableTargets.sort((a, b) => a.layerOrder - b.layerOrder);
                targets.forEach(target => {
                    delete target.layerOrder;
                });
                if (wholeProject) {
                    vm.refreshWorkspace();
                    vm.setCompatibilityMode(true);
                    
                    // 自动选中第一个非舞台角色并居中显示积木
                    setTimeout(() => {
                        const nonStageTargets = vm.runtime.targets.filter(t => !t.isStage);
                        if (nonStageTargets.length > 0) {
                            const firstTarget = nonStageTargets[0];
                            console.log("[DEBUG] 自动选中角色:", firstTarget.getName());
                            vm.setEditingTarget(firstTarget.id);
                            
                            // 等待积木渲染完成后，自动居中显示
                            setTimeout(() => {
                                // 获取积木工作区并居中显示所有积木
                                try {
                                    const blocksWorkspace = document.querySelector('.blocklyWorkspace');
                                    if (blocksWorkspace && blocksWorkspace.workspace) {
                                        const workspace = blocksWorkspace.workspace;
                                        
                                        // 获取所有顶部积木块
                                        const allBlocks = workspace.getTopBlocks(true);
                                        if (allBlocks.length > 0) {
                                            // 计算所有积木的边界框
                                            let minX = Infinity;
                                            let maxX = -Infinity;
                                            
                                            allBlocks.forEach(block => {
                                                const blockXY = block.getRelativeToSurfaceXY();
                                                const width = block.width || 100;
                                                
                                                if (blockXY.x < minX) minX = blockXY.x;
                                                if (blockXY.x + width > maxX) maxX = blockXY.x + width;
                                            });
                                            
                                            // 获取视口宽度
                                            const metrics = workspace.getMetrics();
                                            if (metrics) {
                                                const viewWidth = metrics.viewWidth;
                                                const contentWidth = maxX - minX;
                                                
                                                // 计算水平居中需要的偏移
                                                // 添加一个额外的偏移量让积木更靠左居中
                                                const extraOffset = 50; // 额外向左偏移50像素
                                                const targetScrollX = minX + (contentWidth / 2) - (viewWidth / 2) - extraOffset;
                                                
                                                // 垂直居中（已有的逻辑）
                                                const contentHeight = metrics.contentHeight;
                                                const viewHeight = metrics.viewHeight;
                                                const targetScrollY = (contentHeight - viewHeight) / 2;
                                                
                                                // 设置滚动位置（取负值因为滚动方向相反）
                                                workspace.scrollX(-targetScrollX);
                                                workspace.scrollY(-targetScrollY);
                                                workspace.resize();
                                                
                                                console.log("[DEBUG] 积木工作区已自动居中", {
                                                    minX,
                                                    maxX,
                                                    contentWidth,
                                                    viewWidth,
                                                    extraOffset,
                                                    scrollX: -targetScrollX,
                                                    scrollY: -targetScrollY
                                                });
                                            }
                                        }
                                    }
                                } catch (error) {
                                    console.warn("[DEBUG] 自动居中失败:", error);
                                }
                            }, 300);
                        }
                    }, 200);
                }
                return targets;
            });
        };
        
        console.log("[DEBUG] 已应用 VM installTargets 补丁");
    }
    componentDidUpdate(prevProps) {
        if (this.props.projectId !== prevProps.projectId) {
            if (this.props.projectId !== null) {
                this.props.onUpdateProjectId(this.props.projectId);
            }
            setProjectIdMetadata(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
        if (this.props.shouldStopProject && !prevProps.shouldStopProject) {
            this.props.vm.stopAll();
        }
    }
    render() {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`
            );
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isScratchDesktop,
            isShowingProject,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onProjectLoaded: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shouldStopProject: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired,
};

GUI.defaultProps = {
    isScratchDesktop: false,
    isTotallyNormal: false,
    onStorageInit: (storageInstance) =>
        storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {},
};

const mapStateToProps = (state) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        // backdropLibraryVisible 已移除 - 不需要背景库功能
        // backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible:
            state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        // cardsVisible 已移除 - 不需要卡片教程功能
        // cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        // costumeLibraryVisible 已移除 - 不需要服装库功能
        // costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        // costumesTabVisible 已移除 - 不需要造型 Tab
        // costumesTabVisible:
        //     state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        // debugModalVisible 已移除 - 不需要调试模态框
        // debugModalVisible: state.scratchGui.modals.debugModal,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        locale: state.locales.locale,
        projectId: state.scratchGui.projectState.projectId,
        // soundsTabVisible 已移除 - 不需要声音 Tab
        // soundsTabVisible:
        //     state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage:
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id ===
                state.scratchGui.targets.editingTarget,
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        // tipsLibraryVisible 已移除 - 不需要教程库功能
        // tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm,
    };
};

const mapDispatchToProps = (dispatch) => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: (tab) => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseDebugModal: () => dispatch(closeDebugModal()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal()),
});

const ConnectedGUI = injectIntl(
    connect(mapStateToProps, mapDispatchToProps)(GUI)
);

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC("Top Level App"),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC,
    systemPreferencesHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
